// src/components/Radiologist/ReaderPortal.jsx - Main Radiologist Workspace
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  Badge,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  ThemeProvider,
  createTheme,
  CssBaseline,
  alpha,
  styled,
  Avatar,
  Divider,
  useMediaQuery,
  Menu,
  MenuItem as MuiMenuItem,
  LinearProgress,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  LocalHospital,
  Visibility,
  Assignment,
  CheckCircle,
  Flag,
  Schedule,
  Warning,
  PriorityHigh,
  Refresh,
  Search,
  FilterList,
  Brightness4,
  Brightness7,
  AccountBox,
  Logout as LogoutIcon,
  Person,
  Settings,
  Key as KeyIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  ChevronLeft,
  Close,
  Help,
  AssignmentInd as MyStudiesIcon,
  Groups as AllStudiesIcon,
  CheckCircleOutline as CompletedTodayIcon,
  AccessTime as ClaimedAtIcon,
  OpenInNew as ViewReportIcon,
  RateReview as QAReviewIcon,
  ChatBubbleOutline as ChatIcon,
  Lock,
  LockOpen,
  Edit,
} from '@mui/icons-material';
import DirectMessagesView from '../Common/DirectMessagesView';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import {
  getAvailableForClaiming, getMyAssignedStudies, claimStudy, unclaimStudy,
  getAllStudiesForRadiologist, getMyRadiologistStats,
  getAssignedHospitals, getAssignedHospitalStudies,
} from '../../services/reporting';
import { formatPatientName, generateOHIFUrl, formatTatMinutes } from '../../services/utils';
import DycovueReportForm from './DycovueReportForm';
import QAQueue from './QAQueue';
import ReportStatusBadge from '../Common/ReportStatusBadge';
import ReportViewer from '../Common/ReportViewer';
import FullLogo from '../logo/FullLogo';
import ProfileSettings from '../Admin/ProfileSettings';
import UserSettings from '../UserSettings';
import HelpSupport from '../Admin/HelpSupport';
import ChangePasswordDialog from '../Auth/ChangePassword';
import LabReportModal from './LabReportModal';
import ClaimStudyModal from '../Common/ClaimStudyModal';
import { getUnreadNotifications, markAllNotificationsAsRead } from '../../services/notifications';
import NotificationListener from '../Common/NotificationListener';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

// Tesla-inspired color palette
const teslaColors = {
  dark: {
    primary: '#3E6AE1',
    secondary: '#34C3FF',
    background: '#121212',
    surface: '#1E1E1E',
    card: 'rgba(30, 30, 30, 0.8)',
    text: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.7)',
    border: 'rgba(255, 255, 255, 0.12)',
  },
  light: {
    primary: '#3E6AE1',
    secondary: '#34C3FF',
    background: '#F5F5F7',
    surface: '#FFFFFF',
    card: 'rgba(255, 255, 255, 0.8)',
    text: '#121212',
    textSecondary: 'rgba(18, 18, 18, 0.7)',
    border: 'rgba(0, 0, 0, 0.12)',
  }
};

// Performant surface styled components
const GlassPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.08)' 
    : 'rgba(0, 0, 0, 0.08)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 2px 8px rgba(0, 0, 0, 0.25)'
    : '0 2px 8px rgba(0, 0, 0, 0.05)',
  backgroundImage: 'none',
}));

const GlowButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${teslaColors.dark.primary}, ${teslaColors.dark.secondary})`,
  border: 0,
  borderRadius: '12px',
  color: 'white',
  padding: '10px 24px',
  boxShadow: `0 0 15px ${alpha(teslaColors.dark.primary, 0.5)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: `0 0 20px ${alpha(teslaColors.dark.primary, 0.8)}`,
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 16px',
    fontSize: '0.875rem',
  },
}));

const drawerWidth = 260;
const collapsedDrawerWidth = 68;

const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedDrawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    width: open ? drawerWidth : collapsedDrawerWidth,
    height: '100%',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    background: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isDark' && prop !== 'drawerOpen',
})(({ theme, selected, isDark, drawerOpen }) => ({
  borderRadius: '8px',
  margin: drawerOpen ? '4px 10px' : '4px auto',
  marginBottom: '6px',
  padding: drawerOpen ? '10px 14px' : '10px 0',
  width: drawerOpen ? 'calc(100% - 20px)' : '44px',
  height: '44px',
  minHeight: '44px',
  justifyContent: drawerOpen ? 'initial' : 'center',
  alignItems: 'center',
  transition: 'all 0.2s ease',
  boxSizing: 'border-box',
  '&:hover': {
    backgroundColor: isDark 
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.primary.main, 0.08),
  },
  ...(selected && {
    backgroundColor: isDark 
      ? alpha(theme.palette.primary.main, 0.22)
      : theme.palette.primary.main,
    color: isDark ? theme.palette.primary.main : 'white',
    '& .MuiListItemIcon-root': {
      color: isDark ? theme.palette.primary.main : 'white',
    },
    '&:hover': {
      backgroundColor: isDark 
        ? alpha(theme.palette.primary.main, 0.28)
        : theme.palette.primary.dark,
    },
  }),
}));

const LogoBox = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(4.5),
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  cursor: 'pointer',
}));

// Create theme function
const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: { main: teslaColors.light.primary },
          secondary: { main: teslaColors.light.secondary },
          background: { default: teslaColors.light.background, paper: teslaColors.light.surface },
          text: { primary: teslaColors.light.text, secondary: teslaColors.light.textSecondary },
          divider: teslaColors.light.border,
        }
      : {
          primary: { main: teslaColors.dark.primary },
          secondary: { main: teslaColors.dark.secondary },
          background: { default: teslaColors.dark.background, paper: teslaColors.dark.surface },
          text: { primary: teslaColors.dark.text, secondary: teslaColors.dark.textSecondary },
          divider: teslaColors.dark.border,
        }),
  },
  typography: {
    fontFamily: '"Satoshi", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h6: { fontWeight: 600 },
    body1: { fontWeight: 400, lineHeight: 1.5 },
    body2: { fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiButton: { styleOverrides: { root: { textTransform: 'none', borderRadius: '12px', fontWeight: 500 } } },
    MuiTableCell: { styleOverrides: { root: { borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}` } } },
  },
});

// Color mode context
export const ColorModeContext = React.createContext({ toggleColorMode: () => {} });

const TabPanel = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 0.5 }}>{children}</Box>}
    </div>
  );
};

const StudyCard = ({ study, onClaim, onView, claimed = false }) => {
  const [claiming, setClaiming] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClaim = async () => {
    setClaiming(true);
    try {
      await onClaim(study);
    } catch (error) {
      console.error('Claim error:', error);
    } finally {
      setClaiming(false);
    }
  };

  const getPriorityColor = () => {
    return study.dycovuePriority === 'stat' ? 'error' : 'default';
  };

  const getPriorityLabel = () => {
    return study.dycovuePriority === 'stat' ? 'STAT (within 1 hr)' : 'Routine';
  };

  const getPriorityIcon = () => {
    return study.dycovuePriority === 'stat' ? <PriorityHigh /> : null;
  };

  const getTimeElapsed = () => {
    if (!study.dycovueRequestedAt) return '';
    const elapsed = Date.now() - new Date(study.dycovueRequestedAt).getTime();
    const hours = Math.floor(elapsed / 1000 / 60 / 60);
    const minutes = Math.floor((elapsed / 1000 / 60) % 60);
    return `${hours}h ${minutes}m ago`;
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '12px' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Priority Badge */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Chip
            label={getPriorityLabel()}
            color={getPriorityColor()}
            icon={getPriorityIcon()}
            size="small"
          />
          <Typography variant="caption" color="textSecondary">
            {getTimeElapsed()}
          </Typography>
        </Box>

        {/* Patient Info */}
        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom noWrap fontWeight="bold">
          {formatPatientName(study.patientName)}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          ID: {study.patientId}
        </Typography>

        {/* Study Info */}
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} mb={1} flexWrap="wrap">
            <Chip label={study.modality} size="small" color="primary" variant="outlined" />
            <Chip 
              label={`${study.totalInstances || 0} images`} 
              size="small" 
              variant="outlined" 
            />
          </Stack>
          <Typography variant="body2" noWrap>
            {study.studyDescription || 'No description'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Study Date: {study.studyDate ? new Date(study.studyDate).toLocaleDateString() : 'Unknown'}
          </Typography>
        </Box>

        {/* Clinical Question */}
        {study.dycovueClinicalQuestion && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="caption" color="primary" fontWeight="bold">
              Clinical Question:
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {study.dycovueClinicalQuestion}
            </Typography>
          </Box>
        )}

        {/* Requested By */}
        {study.dycovueRequestedBy && (
          <Typography variant="caption" color="textSecondary" display="block" mt={2}>
            Requested by: {study.dycovueRequestedBy.firstName} {study.dycovueRequestedBy.lastName}
          </Typography>
        )}

        {/* Status for claimed studies */}
        {claimed && (
          <Box sx={{ mt: 2 }}>
            <ReportStatusBadge status={study.dycovueReportStatus} />
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        {!claimed ? (
          <Button
            fullWidth
            variant="contained"
            startIcon={claiming ? <CircularProgress size={20} /> : <Assignment />}
            onClick={handleClaim}
            disabled={claiming}
            sx={{ borderRadius: '12px' }}
          >
            {claiming ? 'Claiming...' : 'Claim & Report'}
          </Button>
        ) : (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => onView(study)}
            sx={{ borderRadius: '12px' }}
          >
            View & Report
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

const MemoizedStudyCard = React.memo(StudyCard);

const ReaderPortal = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [availableStudies, setAvailableStudies] = useState([]);
  const [myStudies, setMyStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [reportFormOpen, setReportFormOpen] = useState(false);
  const [filters, setFilters] = useState({
    priority: 'all',
    modality: 'all',
    year: 'all',
    search: ''
  });
  const { mode, toggleTheme: sharedToggleTheme } = useThemeMode();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState('portal');
  const [directMessageTargetUser, setDirectMessageTargetUser] = useState(null);
  const [directMessageContext, setDirectMessageContext] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [radiologistStats, setRadiologistStats] = useState(null);
  const [allStudies, setAllStudies] = useState([]);
  const [allStudiesLoading, setAllStudiesLoading] = useState(false);
  const [myStudiesIncludeCompleted, setMyStudiesIncludeCompleted] = useState(false);
  // Keep a ref in sync with the "include completed" toggle so background
  // re-fetches (30s poll, Orthanc onSyncComplete) -- which capture a stale
  // `loadData` closure -- still honor the user's current filter choice.
  const includeCompletedRef = React.useRef(false);
  includeCompletedRef.current = myStudiesIncludeCompleted;
  const notificationButtonRef = React.useRef(null);
  const profileButtonRef = React.useRef(null);
  const [selectedLabReportId, setSelectedLabReportId] = useState(null);
  const [selectedLabPatientName, setSelectedLabPatientName] = useState('');
  const [labReportModalOpen, setLabReportModalOpen] = useState(false);
  const [assignedHospitals, setAssignedHospitals] = useState([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState('all');
  const [assignedHospitalStudies, setAssignedHospitalStudies] = useState([]);
  const [assignedHospitalStudiesLoading, setAssignedHospitalStudiesLoading] = useState(false);
  const [assignedHospitalFilterStatus, setAssignedHospitalFilterStatus] = useState('all');
  const [pageMyStudies, setPageMyStudies] = useState(0);
  const [rowsPerPageMyStudies, setRowsPerPageMyStudies] = useState(20);
  const [pageAssigned, setPageAssigned] = useState(0);
  const [rowsPerPageAssigned, setRowsPerPageAssigned] = useState(20);
  const [pageAllStudies, setPageAllStudies] = useState(0);
  const [rowsPerPageAllStudies, setRowsPerPageAllStudies] = useState(20);
  const [reportViewerOpen, setReportViewerOpen] = useState(false);
  const [reportViewerStudy, setReportViewerStudy] = useState(null);

  const handleOpenReportViewer = useCallback((study) => {
    setReportViewerStudy(study);
    setReportViewerOpen(true);
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: sharedToggleTheme,
    }),
    [sharedToggleTheme],
  );

  const themeObj = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [isMobile]);

  const fetchNotifications = async () => {
    try {
      const data = await getUnreadNotifications();
      setUnreadCount(data.count || 0);
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleNotificationReceived = useCallback(async (data) => {
    await fetchNotifications();
  }, []);

  const handleNotificationMenuOpen = async (event) => {
    setNotificationAnchorEl(event?.currentTarget || notificationButtonRef.current);
    await fetchNotifications();
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      await fetchNotifications();
      handleNotificationMenuClose();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event?.currentTarget || profileButtonRef.current);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleMenuClick = (item) => {
    setCurrentView(item.key);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const menuItems = React.useMemo(() => [
    { key: 'portal', text: 'Reader Portal', icon: <LocalHospital /> },
    { key: 'messages', text: 'Messages', icon: <ChatIcon /> },
    { key: 'profile', text: 'Profile', icon: <Person /> },
    { key: 'settings', text: 'Settings', icon: <Settings /> },
    { key: 'help', text: 'Help & Support', icon: <Help /> },
  ], []);

  const loadAvailableStudies = async () => {
    try {
      const data = await getAvailableForClaiming();
      setAvailableStudies(data.studies || []);
    } catch (err) {
      console.error('Load available studies error:', err);
      setError('Failed to load available studies');
    }
  };

  const loadMyStudies = async (includeCompleted = includeCompletedRef.current) => {
    try {
      const data = await getMyAssignedStudies({ includeCompleted: includeCompleted || undefined });
      setMyStudies(data.studies || []);
    } catch (err) {
      console.error('Load my studies error:', err);
      setError('Failed to load assigned studies');
    }
  };

  const loadRadiologistStats = async () => {
    try {
      const data = await getMyRadiologistStats();
      setRadiologistStats(data);
    } catch (err) {
      console.error('Load radiologist stats error:', err);
    }
  };

  const loadAllStudies = async () => {
    setAllStudiesLoading(true);
    try {
      const data = await getAllStudiesForRadiologist({ limit: 500 });
      setAllStudies(data.studies || []);
    } catch (err) {
      console.error('Load all studies error:', err);
    } finally {
      setAllStudiesLoading(false);
    }
  };

  const loadAssignedHospitals = async () => {
    try {
      const data = await getAssignedHospitals();
      setAssignedHospitals(data.hospitals || []);
    } catch (err) {
      console.error('Load assigned hospitals error:', err);
    }
  };

  const loadAssignedHospitalStudies = async (hospitalId = selectedHospitalId, status = assignedHospitalFilterStatus) => {
    setAssignedHospitalStudiesLoading(true);
    try {
      const params = { limit: 200 };
      if (hospitalId && hospitalId !== 'all') params.facilityId = hospitalId;
      if (status && status !== 'all') params.status = status;
      const data = await getAssignedHospitalStudies(params);
      setAssignedHospitalStudies(data.studies || []);
    } catch (err) {
      console.error('Load assigned hospital studies error:', err);
    } finally {
      setAssignedHospitalStudiesLoading(false);
    }
  };

  const loadData = async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    setError('');
    try {
      // Load available studies, user's assigned reading queue, workload stats, and assigned hospitals concurrently
      await Promise.all([
        loadAvailableStudies(),
        loadMyStudies(),
        loadRadiologistStats(),
        loadAssignedHospitals(),
      ]);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    loadData(false);
    fetchNotifications();
  }, []);

  // Background poll only when portal view is active — avoids needless re-renders
  // that make Profile/Settings feel laggy
  useEffect(() => {
    if (currentView !== 'portal') return;
    const interval = setInterval(() => loadData(true), 30000);
    return () => clearInterval(interval);
  }, [currentView]);

  useEffect(() => {
    if (!user?._id) return;
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [user?._id]);

  useEffect(() => {
    const handleOpenDirectMessage = (e) => {
      const detail = e?.detail || {};
      if (detail.user) {
        setDirectMessageTargetUser(detail.user);
        setDirectMessageContext(detail.context || null);
      }
      setReportFormOpen(false);
      setSelectedStudy(null);
      setCurrentView('messages');
    };
    window.addEventListener('open-direct-message', handleOpenDirectMessage);
    return () => window.removeEventListener('open-direct-message', handleOpenDirectMessage);
  }, []);

  const [claimModalState, setClaimModalState] = useState({ open: false, study: null });

  const handleConfirmClaimFromModal = useCallback(async (study) => {
    try {
      await claimStudy(study._id || study.pacsStudyId);
      await loadData();
      if (selectedHospitalId) {
        await loadAssignedHospitalStudies(selectedHospitalId, assignedHospitalFilterStatus);
      }
      setActiveTab(0);
      setSelectedStudy(study);
      setReportFormOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to claim study');
      throw err;
    }
  }, [selectedHospitalId, assignedHospitalFilterStatus, loadData, loadAssignedHospitalStudies]);

  const handleConfirmUnclaimFromModal = useCallback(async (study) => {
    try {
      await unclaimStudy(study._id || study.pacsStudyId);
      await loadData();
      if (selectedHospitalId) {
        await loadAssignedHospitalStudies(selectedHospitalId, assignedHospitalFilterStatus);
      }
      setClaimModalState({ open: false, study: null });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unclaim study');
      throw err;
    }
  }, [selectedHospitalId, assignedHospitalFilterStatus, loadData, loadAssignedHospitalStudies]);

  const handleViewOnlyFromModal = useCallback((study) => {
    const uid = study.studyInstanceUID || study.studyUID || study.orthancStudyId;
    if (uid) {
      window.open(generateOHIFUrl(uid), '_blank');
    }
  }, []);

  const handleClaim = useCallback((study) => {
    const currentUserId = (user?._id || user?.id)?.toString();
    const assignedId = (study.assignedRadiologist?._id || study.assignedRadiologist)?.toString();
    const isClaimedByMe = Boolean(assignedId && currentUserId && assignedId === currentUserId);
    const isClaimedByOther = Boolean(
      assignedId &&
      currentUserId &&
      assignedId !== currentUserId &&
      ['claimed', 'in_progress'].includes(study.dycovueReportStatus)
    );

    if (isClaimedByOther) {
      setClaimModalState({ open: true, study });
      return;
    }

    if (isClaimedByMe) {
      setSelectedStudy(study);
      setReportFormOpen(true);
      return;
    }

    // Unclaimed: show claim popup
    setClaimModalState({ open: true, study });
  }, [user]);

  const handleViewStudy = useCallback((study) => {
    const currentUserId = (user?._id || user?.id)?.toString();
    const assignedId = (study.assignedRadiologist?._id || study.assignedRadiologist)?.toString();
    const isClaimedByMe = Boolean(assignedId && currentUserId && assignedId === currentUserId);
    const isClaimedByOther = Boolean(
      assignedId &&
      currentUserId &&
      assignedId !== currentUserId &&
      ['claimed', 'in_progress'].includes(study.dycovueReportStatus)
    );
    const isCompleted = ['completed', 'approved'].includes(study.dycovueReportStatus);

    if (isClaimedByOther) {
      setClaimModalState({ open: true, study });
      return;
    }

    if (isCompleted) {
      handleOpenReportViewer(study);
      return;
    }

    if (isClaimedByMe) {
      setSelectedStudy(study);
      setReportFormOpen(true);
      return;
    }

    // Unclaimed: show claim modal
    setClaimModalState({ open: true, study });
  }, [user, handleOpenReportViewer]);

  const handleClaimAndReportAssigned = useCallback((study) => {
    const currentUserId = (user?._id || user?.id)?.toString();
    const assignedId = (study.assignedRadiologist?._id || study.assignedRadiologist)?.toString();
    const isClaimedByMe = Boolean(assignedId && currentUserId && assignedId === currentUserId);
    const isClaimedByOther = Boolean(
      assignedId &&
      currentUserId &&
      assignedId !== currentUserId &&
      ['claimed', 'in_progress'].includes(study.dycovueReportStatus)
    );
    const isCompleted = ['completed', 'approved'].includes(study.dycovueReportStatus);

    if (isClaimedByOther) {
      setClaimModalState({ open: true, study });
      return;
    }

    if (isCompleted) {
      handleOpenReportViewer(study);
      return;
    }

    if (isClaimedByMe) {
      setSelectedStudy(study);
      setReportFormOpen(true);
      return;
    }

    // Unclaimed: show claim modal
    setClaimModalState({ open: true, study });
  }, [user, handleOpenReportViewer]);

  const handleReportSuccess = useCallback(() => {
    setReportFormOpen(false);
    setSelectedStudy(null);
    loadData();
    loadAssignedHospitalStudies(selectedHospitalId, assignedHospitalFilterStatus);
  }, [selectedHospitalId, assignedHospitalFilterStatus, loadData, loadAssignedHospitalStudies]);

  const filterStudies = useCallback((studies) => {
    return studies.filter(study => {
      if (filters.priority !== 'all' && study.dycovuePriority !== filters.priority) {
        return false;
      }
      if (filters.modality !== 'all' && study.modality !== filters.modality) {
        return false;
      }
      if (filters.year && filters.year !== 'all') {
        const targetYear = parseInt(filters.year, 10);
        const d = study.studyDate ? new Date(study.studyDate) : (study.createdAt ? new Date(study.createdAt) : null);
        if (!d || isNaN(d.getTime()) || d.getFullYear() !== targetYear) {
          return false;
        }
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase().trim();
        const isYearQuery = /^\d{4}$/.test(searchLower);
        if (isYearQuery) {
          const yr = parseInt(searchLower, 10);
          const d = study.studyDate ? new Date(study.studyDate) : (study.createdAt ? new Date(study.createdAt) : null);
          const matchesYear = d && !isNaN(d.getTime()) && d.getFullYear() === yr;
          const matchesText = study.patientName?.toLowerCase().includes(searchLower) ||
            study.patientId?.toLowerCase().includes(searchLower) ||
            study.studyDescription?.toLowerCase().includes(searchLower);
          return matchesYear || matchesText;
        }
        return (
          study.patientName?.toLowerCase().includes(searchLower) ||
          study.patientId?.toLowerCase().includes(searchLower) ||
          study.studyDescription?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [filters]);

  const filteredAvailable = React.useMemo(() => filterStudies(availableStudies), [availableStudies, filterStudies]);
  const filteredMyStudies = React.useMemo(() => filterStudies(myStudies), [myStudies, filterStudies]);

  const totalAssignedPending = React.useMemo(() => {
    return assignedHospitals.reduce((acc, h) => acc + (h.pendingStudies || 0), 0);
  }, [assignedHospitals]);

  const filteredAssignedStudies = React.useMemo(() => {
    return assignedHospitalStudies.filter(study => {
      if (filters.priority !== 'all' && study.dycovuePriority !== filters.priority) {
        return false;
      }
      if (filters.modality !== 'all' && study.modality !== filters.modality) {
        return false;
      }
      if (filters.year && filters.year !== 'all') {
        const targetYear = parseInt(filters.year, 10);
        const d = study.studyDate ? new Date(study.studyDate) : (study.createdAt ? new Date(study.createdAt) : null);
        if (!d || isNaN(d.getTime()) || d.getFullYear() !== targetYear) {
          return false;
        }
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase().trim();
        const isYearQuery = /^\d{4}$/.test(searchLower);
        const facilityName = (study.facility?.facilityName || study.facility?.name || '').toLowerCase();
        if (isYearQuery) {
          const yr = parseInt(searchLower, 10);
          const d = study.studyDate ? new Date(study.studyDate) : (study.createdAt ? new Date(study.createdAt) : null);
          const matchesYear = d && !isNaN(d.getTime()) && d.getFullYear() === yr;
          const matchesText = study.patientName?.toLowerCase().includes(searchLower) ||
            study.patientId?.toLowerCase().includes(searchLower) ||
            study.studyDescription?.toLowerCase().includes(searchLower) ||
            facilityName.includes(searchLower);
          return matchesYear || matchesText;
        }
        return (
          study.patientName?.toLowerCase().includes(searchLower) ||
          study.patientId?.toLowerCase().includes(searchLower) ||
          study.studyDescription?.toLowerCase().includes(searchLower) ||
          facilityName.includes(searchLower)
        );
      }
      return true;
    });
  }, [assignedHospitalStudies, filters.priority, filters.modality, filters.year, filters.search]);

  const filteredAllStudies = React.useMemo(() => filterStudies(allStudies), [allStudies, filterStudies]);

  const paginatedMyStudies = React.useMemo(() => {
    return filteredMyStudies.slice(
      pageMyStudies * rowsPerPageMyStudies,
      pageMyStudies * rowsPerPageMyStudies + rowsPerPageMyStudies
    );
  }, [filteredMyStudies, pageMyStudies, rowsPerPageMyStudies]);

  const paginatedAssignedStudies = React.useMemo(() => {
    return filteredAssignedStudies.slice(
      pageAssigned * rowsPerPageAssigned,
      pageAssigned * rowsPerPageAssigned + rowsPerPageAssigned
    );
  }, [filteredAssignedStudies, pageAssigned, rowsPerPageAssigned]);

  const paginatedAllStudies = React.useMemo(() => {
    return filteredAllStudies.slice(
      pageAllStudies * rowsPerPageAllStudies,
      pageAllStudies * rowsPerPageAllStudies + rowsPerPageAllStudies
    );
  }, [filteredAllStudies, pageAllStudies, rowsPerPageAllStudies]);

  useEffect(() => {
    setPageMyStudies(0);
    setPageAssigned(0);
    setPageAllStudies(0);
  }, [filters]);

  // Stats — memoised to avoid recalculating on every render
  const stats = React.useMemo(() => ({
    available: availableStudies.length,
    claimed: myStudies.filter(s => s.dycovueReportStatus === 'claimed').length,
    inProgress: myStudies.filter(s => s.dycovueReportStatus === 'in_progress').length,
    stat: availableStudies.filter(s => s.dycovuePriority === 'stat').length,
    myActive: myStudies.filter(s => ['claimed', 'in_progress'].includes(s.dycovueReportStatus)).length,
  }), [availableStudies, myStudies]);
  const capacityPct = radiologistStats?.maxCapacity
    ? Math.min(100, Math.round((radiologistStats.currentWorkload / radiologistStats.maxCapacity) * 100))
    : 0;
  const canViewAllStudies = radiologistStats?.allowAllStudiesView === true || user?.role === 'admin';

  if (loading && availableStudies.length === 0 && myStudies.length === 0) {
    return (
      <Box sx={{ width: '100%', position: 'fixed', top: 0, left: 0 }}>
        <LinearProgress
          sx={{
            height: '3px',
            backgroundColor: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: `linear-gradient(45deg, ${teslaColors.dark.primary}, ${teslaColors.dark.secondary})`,
            },
          }}
        />
      </Box>
    );
  }

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={themeObj}>
        <CssBaseline />
        {/* Notification Listener */}
        {user?._id && (
          <NotificationListener 
            userId={user._id} 
            onNotificationReceived={handleNotificationReceived}
            onSyncComplete={() => loadData(true)}
          />
        )}
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: themeObj.palette.mode === 'dark' ? '#121212' : '#F5F5F7', position: 'relative', zIndex: 1 }}>
          <AppBar
            position="fixed"
            sx={{
              zIndex: themeObj.zIndex.drawer + 1,
              background: themeObj.palette.background.paper,
              boxShadow: 'none',
              borderBottom: `1px solid ${themeObj.palette.divider}`,
            }}
          >
            <Toolbar>
              <IconButton 
                onClick={handleDrawerToggle} 
                edge="start" 
                sx={{ 
                  mr: 2,
                  color: themeObj.palette.text.primary,
                  '&:hover': {
                    backgroundColor: themeObj.palette.action.hover,
                  }
                }}
              >
                {drawerOpen ? <ChevronLeft /> : <MenuIcon />}
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <FullLogo />
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton 
                onClick={colorMode.toggleColorMode} 
                sx={{ 
                  mr: 1,
                  color: themeObj.palette.text.primary,
                  '&:hover': {
                    backgroundColor: themeObj.palette.action.hover,
                  }
                }}
              >
                {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
              <IconButton 
                ref={notificationButtonRef} 
                onClick={handleNotificationMenuOpen} 
                sx={{ 
                  mr: 1,
                  color: themeObj.palette.text.primary,
                  '&:hover': {
                    backgroundColor: themeObj.palette.action.hover,
                  }
                }}
              >
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.1)' },
                        '100%': { transform: 'scale(1)' },
                      },
                    },
                  }}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <Menu
                anchorEl={notificationAnchorEl}
                open={Boolean(notificationAnchorEl)}
                onClose={handleNotificationMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    width: { xs: 'calc(100vw - 32px)', sm: 400 },
                    maxWidth: { xs: 'calc(100vw - 32px)', sm: 400 },
                    maxHeight: 520,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    borderRadius: '14px',
                    boxShadow: themeObj.palette.mode === 'dark'
                      ? '0 12px 36px rgba(0,0,0,0.6)'
                      : '0 12px 36px rgba(0,0,0,0.12)',
                    border: `1px solid ${themeObj.palette.divider}`,
                  },
                }}
              >
                <Box sx={{ p: 2, borderBottom: `1px solid ${themeObj.palette.divider}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                      Notifications
                    </Typography>
                    {unreadCount > 0 && (
                      <Button size="small" onClick={handleMarkAllAsRead} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                        Mark all read
                      </Button>
                    )}
                  </Box>
                </Box>
                {notifications.length > 0 ? (
                  notifications.map((notification, index) => (
                    <MuiMenuItem
                      key={notification._id || index}
                      onClick={() => {
                        if (notification.type === 'lab_report_ready') {
                          setSelectedLabReportId(notification.labReportId || notification.labReportOrderId);
                          setSelectedLabPatientName(notification.patientName);
                          setLabReportModalOpen(true);
                        } else if (notification.type === 'report_flagged') {
                          setActiveTab(canViewAllStudies ? 3 : 2);
                          loadAssignedHospitalStudies();
                        } else if (notification.studyUID) {
                          navigate(`/viewer/${notification.studyUID}?reportId=${notification.reportId}`);
                        } else {
                          setActiveTab(1);
                        }
                        handleNotificationMenuClose();
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        whiteSpace: 'normal',
                        alignItems: 'flex-start',
                        borderBottom: index < notifications.length - 1 ? `1px solid ${themeObj.palette.divider}` : 'none',
                        '&:hover': {
                          backgroundColor: themeObj.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Box sx={{ width: '100%', minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: notification.read ? 400 : 600,
                            mb: 0.5,
                            color: notification.type === 'report_flagged' ? '#EF4444' : 'inherit',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            lineHeight: 1.45,
                          }}
                        >
                          {notification.type === 'lab_report_ready' 
                            ? '🧪 ' 
                            : notification.type === 'report_flagged'
                              ? '🚩 '
                              : (notification.priority === 'stat' ? '🚨 ' : '🔔 ')}
                          {(notification.message || (notification.type === 'lab_report_ready' ? 'Lab Report Ready' : (notification.type === 'report_flagged' ? 'Report Concerns Flagged' : 'Report Ready'))).replace(/\^/g, ' ')}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="textSecondary"
                          sx={{
                            display: 'block',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.3,
                          }}
                        >
                          {formatPatientName(notification.patientName)} • {notification.type === 'lab_report_ready' ? 'Laboratory Department' : (notification.modality || 'Imaging')}
                        </Typography>
                        {notification.timestamp && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.72rem' }}>
                            {new Date(notification.timestamp).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    </MuiMenuItem>
                  ))
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      No new notifications
                    </Typography>
                  </Box>
                )}
              </Menu>
              <IconButton 
                ref={profileButtonRef} 
                onClick={handleProfileMenuOpen}
                sx={{
                  color: themeObj.palette.text.primary,
                  '&:hover': {
                    backgroundColor: themeObj.palette.action.hover,
                  }
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'primary.main',
                    fontSize: '0.875rem',
                  }}
                >
                  {user?.firstName?.charAt(0)}
                  {user?.lastName?.charAt(0)}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={profileAnchorEl}
                open={Boolean(profileAnchorEl)}
                onClose={handleProfileMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MuiMenuItem onClick={handleProfileMenuClose}>
                  <Avatar sx={{ width: 24, height: 24, mr: 2, bgcolor: themeObj.palette.primary.main }}>
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {user?.firstName} {user?.lastName}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {user?.email}
                    </Typography>
                  </Box>
                </MuiMenuItem>
                <Divider />
                <MuiMenuItem
                  onClick={() => {
                    setCurrentView('profile');
                    handleProfileMenuClose();
                  }}
                >
                  <Person fontSize="small" sx={{ mr: 2 }} />
                  Profile
                </MuiMenuItem>
                <MuiMenuItem
                  onClick={() => {
                    setChangePasswordOpen(true);
                    handleProfileMenuClose();
                  }}
                >
                  <KeyIcon fontSize="small" sx={{ mr: 2 }} />
                  Change Password
                </MuiMenuItem>
                <MuiMenuItem
                  onClick={() => {
                    setCurrentView('settings');
                    handleProfileMenuClose();
                  }}
                >
                  <Settings fontSize="small" sx={{ mr: 2 }} />
                  Settings
                </MuiMenuItem>
                <MuiMenuItem onClick={handleLogout}>
                  <LogoutIcon fontSize="small" sx={{ mr: 2 }} />
                  Logout
                </MuiMenuItem>
              </Menu>
            </Toolbar>
          </AppBar>

          <StyledDrawer variant={isMobile ? 'temporary' : 'permanent'} open={drawerOpen} onClose={handleDrawerToggle}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: drawerOpen ? 1.5 : 0.75,
                pt: { xs: 8, sm: 8 },
                overflowX: 'hidden',
                boxSizing: 'border-box',
                position: 'relative',
              }}
            >
              {/* Close button for mobile */}
              {isMobile && (
                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    zIndex: 10,
                    opacity: 0.5,
                    '&:hover': {
                      opacity: 0.75,
                    },
                    display: { md: 'none' },
                  }}
                >
                  <Close />
                </IconButton>
              )}

              {/* Navigation - flex-1 for middle section */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  mt: 1,
                  minHeight: 0,
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
                    borderRadius: '4px',
                  },
                }}
              >
                <List sx={{ py: 0, px: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {menuItems.map((item) => {
                    const isActive = currentView === item.key;
                    const inactiveColor = mode === 'dark' ? '#CBD5E1' : '#6B7280';
                    const iconColor = isActive 
                      ? (mode === 'dark' ? themeObj.palette.primary.main : 'white')
                      : inactiveColor;
                    
                    return (
                      <Tooltip key={item.text} title={!drawerOpen ? item.text : ''} placement="right" arrow>
                        <StyledListItemButton 
                          selected={isActive} 
                          onClick={() => handleMenuClick(item)}
                          isDark={mode === 'dark'}
                          drawerOpen={drawerOpen}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: drawerOpen ? 36 : 0,
                              mr: drawerOpen ? 1.5 : 0,
                              color: iconColor,
                              justifyContent: 'center',
                              alignItems: 'center',
                              '& .MuiSvgIcon-root': {
                                fontSize: '1.25rem',
                              },
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          {drawerOpen && (
                            <ListItemText
                              primary={item.text}
                              primaryTypographyProps={{
                                fontWeight: isActive ? 600 : 500,
                                fontSize: '0.85rem',
                                color: isActive 
                                  ? (mode === 'dark' ? themeObj.palette.primary.main : 'white')
                                  : inactiveColor,
                              }}
                            />
                          )}
                        </StyledListItemButton>
                      </Tooltip>
                    );
                  })}
                </List>
              </Box>

              {/* Divider and Logout section at bottom */}
              <Box sx={{ mt: 'auto', pt: 1, pb: 1, flexShrink: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Divider sx={{ my: 1.5, width: '100%', borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)' }} />
                <Tooltip title={!drawerOpen ? 'Logout' : ''} placement="right" arrow>
                  <Button
                    onClick={handleLogout}
                    sx={{
                      width: drawerOpen ? 'calc(100% - 16px)' : 44,
                      minWidth: drawerOpen ? 'auto' : 44,
                      height: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: drawerOpen ? 'flex-start' : 'center',
                      px: drawerOpen ? 2 : 0,
                      py: 1,
                      textTransform: 'none',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#EF4444',
                      borderRadius: '8px',
                      gap: drawerOpen ? 1.5 : 0,
                      '&:hover': {
                        backgroundColor: mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)',
                      },
                    }}
                  >
                    <LogoutIcon sx={{ fontSize: '1.25rem', color: '#EF4444' }} />
                    {drawerOpen && (
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#EF4444' }}>
                        Logout
                      </Typography>
                    )}
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          </StyledDrawer>

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: { xs: 0.75, sm: 1 },
              width: { sm: `calc(100% - ${drawerOpen ? drawerWidth : collapsedDrawerWidth}px)` },
              transition: themeObj.transitions.create(['width', 'margin'], {
                easing: themeObj.transitions.easing.sharp,
                duration: themeObj.transitions.duration.enteringScreen,
              }),
            }}
          >
            {/* Spacer sized to the fixed AppBar so content never slides under it */}
            <Toolbar />
            {currentView === 'profile' ? (
              <Container maxWidth="xl" sx={{ position: 'relative', px: isSmallMobile ? 1.5 : 0 }}>
                <ProfileSettings />
              </Container>
            ) : currentView === 'settings' ? (
              <Container maxWidth="xl" sx={{ position: 'relative', px: isSmallMobile ? 1.5 : 0 }}>
                <UserSettings variant="pacs" />
              </Container>
            ) : currentView === 'help' ? (
              <Container maxWidth="xl" sx={{ position: 'relative', px: isSmallMobile ? 1.5 : 0 }}>
                <HelpSupport variant="pacs" />
              </Container>
            ) : currentView === 'messages' ? (
              <Container maxWidth="xl" sx={{ position: 'relative', px: isSmallMobile ? 1.5 : 0 }}>
                <DirectMessagesView
                  initialTargetUser={directMessageTargetUser}
                  initialContext={directMessageContext}
                  onViewReport={(study) => handleOpenReportViewer(study)}
                />
              </Container>
            ) : (
              <Container maxWidth="xl" sx={{ position: 'relative', px: isSmallMobile ? 1.5 : 0 }}>
                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 1, 
                      py: 0.25,
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      background: themeObj.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
                    }} 
                    onClose={() => setError('')}
                  >
                    {error}
                  </Alert>
                )}

                {/* Workload Stats Bar (Compact) */}
                <GlassPaper sx={{ p: '4px 8px', mb: 0.75, borderRadius: '6px', minHeight: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Chip
                      label={`My Active: ${stats.myActive}`}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20 }}
                    />
                    <Chip
                      label={`Completed Today: ${radiologistStats?.completedToday ?? 0}`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20 }}
                    />
                    <Chip
                      label={`Avg TAT: ${formatTatMinutes(radiologistStats?.avgTATMinutes)}`}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20 }}
                    />
                    {stats.stat > 0 && (
                      <Chip
                        label={`STAT Pending: ${stats.stat}`}
                        size="small"
                        color="error"
                        sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20 }}
                      />
                    )}
                    <Chip
                      label={`Dedicated Available: ${totalAssignedPending}`}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 140 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary' }}>
                      Capacity: {radiologistStats ? `${radiologistStats.currentWorkload}/${radiologistStats.maxCapacity}` : '—'}
                    </Typography>
                    <Box sx={{ width: 48 }}>
                      <LinearProgress
                        variant="determinate"
                        value={capacityPct}
                        sx={{
                          height: 4, borderRadius: 2,
                          bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 2,
                            bgcolor: capacityPct >= 90 ? '#EF4444' : capacityPct >= 70 ? '#F59E0B' : '#10B981',
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </GlassPaper>

                {/* Unified Workstation Container */}
                <GlassPaper sx={{ p: 0, overflow: 'hidden', borderRadius: '6px' }}>
                  {/* Tabs & Filter Toolbar */}
                  <Box sx={{ borderBottom: `1px solid ${themeObj.palette.divider}`, bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
                    <Tabs
                      value={activeTab}
                      onChange={(e, newValue) => {
                        setActiveTab(newValue);
                        if (newValue === 1) {
                          loadAssignedHospitalStudies(selectedHospitalId, assignedHospitalFilterStatus);
                        }
                        const allStudiesTabIndex = 2;
                        if (newValue === allStudiesTabIndex && canViewAllStudies && allStudies.length === 0) {
                          loadAllStudies();
                        }
                      }}
                      indicatorColor="primary"
                      textColor="primary"
                      sx={{
                        minHeight: 32,
                        '& .MuiTab-root': {
                          minHeight: 32,
                          py: 0.25,
                          px: { xs: 0.75, sm: 1.5 },
                          fontSize: '0.74rem',
                          fontWeight: 700,
                          textTransform: 'none',
                        }
                      }}
                    >
                      {/* Tab 0: Reading Queue (assigned to this radiologist) */}
                      <Tab
                        icon={<MyStudiesIcon sx={{ fontSize: 14 }} />}
                        iconPosition="start"
                        label={
                          <Badge badgeContent={stats.myActive} color="warning" sx={{ '& .MuiBadge-badge': { fontSize: '0.62rem', height: 16, minWidth: 16 } }}>
                            <Box sx={{ ml: stats.myActive > 0 ? 1 : 0 }}>Reading Queue</Box>
                          </Badge>
                        }
                      />
                      {/* Tab 1: Available Studies (dedicated auto-routed hospital studies) */}
                      <Tab
                        icon={<LocalHospital sx={{ fontSize: 14 }} />}
                        iconPosition="start"
                        label={
                          <Badge badgeContent={totalAssignedPending} color="info" sx={{ '& .MuiBadge-badge': { fontSize: '0.62rem', height: 16, minWidth: 16 } }}>
                            <Box sx={{ ml: totalAssignedPending > 0 ? 1 : 0 }}>Available Studies</Box>
                          </Badge>
                        }
                      />
                      {/* Tab 2: Scan Board — facility-wide PACS studies (permission-gated) */}
                      {canViewAllStudies && (
                        <Tab
                          icon={<AllStudiesIcon sx={{ fontSize: 14 }} />}
                          iconPosition="start"
                          label="Scan Board"
                        />
                      )}
                      {/* QA Review tab — always visible to radiologists/admins */}
                      <Tab
                        icon={<QAReviewIcon sx={{ fontSize: 14 }} />}
                        iconPosition="start"
                        label="QA Review"
                      />
                    </Tabs>

                    {/* Integrated Filter Toolbar */}
                    <Box
                      sx={{
                        p: '4px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                        gap: 0.75,
                        borderTop: `1px solid ${alpha(themeObj.palette.divider, 0.08)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexGrow: 1, maxWidth: { xs: '100%', md: 400 } }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Search patient name, ID, or study..."
                          value={filters.search}
                          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                          InputProps={{
                            startAdornment: <Search sx={{ mr: 0.5, fontSize: 14, color: 'text.secondary' }} />,
                            sx: { height: 26, fontSize: '0.72rem' }
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <FormControl size="small" sx={{ minWidth: 105 }}>
                          <Select
                            value={filters.priority}
                            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                            sx={{ height: 26, fontSize: '0.72rem', borderRadius: '4px' }}
                          >
                            <MenuItem value="all" sx={{ fontSize: '0.72rem' }}>All Priorities</MenuItem>
                            <MenuItem value="stat" sx={{ fontSize: '0.72rem' }}>STAT (within 1 hr)</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 105 }}>
                          <Select
                            value={filters.modality}
                            onChange={(e) => setFilters({ ...filters, modality: e.target.value })}
                            sx={{ height: 26, fontSize: '0.72rem', borderRadius: '4px' }}
                          >
                            <MenuItem value="all" sx={{ fontSize: '0.72rem' }}>All Modalities</MenuItem>
                            <MenuItem value="CT" sx={{ fontSize: '0.72rem' }}>CT</MenuItem>
                            <MenuItem value="MR" sx={{ fontSize: '0.72rem' }}>MRI</MenuItem>
                            <MenuItem value="XR" sx={{ fontSize: '0.72rem' }}>X-Ray</MenuItem>
                            <MenuItem value="US" sx={{ fontSize: '0.72rem' }}>Ultrasound</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 95 }}>
                          <Select
                            value={filters.year || 'all'}
                            onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                            sx={{ height: 26, fontSize: '0.72rem', borderRadius: '4px' }}
                          >
                            <MenuItem value="all" sx={{ fontSize: '0.72rem' }}>All Years</MenuItem>
                            {[2026, 2025, 2024, 2023, 2022, 2021, 2020].map((yr) => (
                              <MenuItem key={yr} value={String(yr)} sx={{ fontSize: '0.72rem' }}>{yr}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Refresh sx={{ fontSize: 13 }} />}
                          onClick={loadData}
                          disabled={loading}
                          sx={{ borderRadius: '4px', height: 26, fontSize: '0.72rem', py: 0, px: 1, textTransform: 'none', fontWeight: 600 }}
                        >
                          Refresh
                        </Button>
                      </Box>
                    </Box>
                  </Box>

                  {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {/* Tab 0: Reading Queue — studies assigned to this radiologist */}
                      <TabPanel value={activeTab} index={0}>
                        <Box sx={{ px: 1.5, py: 0.25, mb: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.72rem' }}>
                            {myStudies.length} {myStudiesIncludeCompleted ? 'total' : 'active'} stud{myStudies.length === 1 ? 'y' : 'ies'} assigned to you
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={myStudiesIncludeCompleted}
                                onChange={(e) => {
                                  setMyStudiesIncludeCompleted(e.target.checked);
                                  loadMyStudies(e.target.checked);
                                }}
                              />
                            }
                            label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Include completed</Typography>}
                          />
                        </Box>
                        {filteredMyStudies.length === 0 ? (
                          <Alert severity="info" sx={{ m: 2, borderRadius: '8px', py: 0.5, fontSize: '0.75rem' }}>
                            {myStudiesIncludeCompleted
                              ? 'No studies assigned to you.'
                              : 'No active studies assigned. Toggle "Include completed" or check the Available tab.'}
                          </Alert>
                        ) : (
                          <>
                            <TableContainer sx={{ px: 0.5 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ '& th': { py: '2px !important', px: '6px !important', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' } }}>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Priority</TableCell>
                                  <TableCell>Patient</TableCell>
                                  <TableCell>Modality</TableCell>
                                  <TableCell>Description</TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Assigned</TableCell>
                                  <TableCell>Images</TableCell>
                                  <TableCell>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {paginatedMyStudies.map((study) => (
                                  <TableRow key={study._id} hover sx={{ '& td': { py: '1.5px !important', px: '6px !important', fontSize: '0.72rem', height: '26px' }, cursor: 'pointer' }} onClick={() => handleViewStudy(study)}>
                                    <TableCell>
                                      {study.dycovuePriority === 'stat' ? (
                                        <Chip label="STAT" color="error" size="small" icon={<PriorityHigh sx={{ fontSize: 12 }} />} sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, px: '2px' }} />
                                      ) : (
                                        <Chip label="Routine" size="small" sx={{ height: 18, fontSize: '0.62rem', px: '2px' }} />
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                         <Box component="span" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
                                           {formatPatientName(study.patientName)}
                                         </Box>
                                         {study.patientId && (
                                           <Box component="span" sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>
                                             #{study.patientId}
                                           </Box>
                                         )}
                                       </Box>
                                     </TableCell>
                                     <TableCell>
                                       <Chip label={study.modality} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.62rem', px: '2px' }} />
                                     </TableCell>
                                     <TableCell>
                                       <Typography variant="body2" noWrap sx={{ maxWidth: 180, fontSize: '0.72rem' }}>
                                         {study.studyDescription || '—'}
                                       </Typography>
                                     </TableCell>
                                    <TableCell>
                                      {(() => {
                                        const isReadingQueueFlagged = study.facilityReviewStatus === 'flagged' || study.dycovueReport?.facilityReview?.status === 'flagged';
                                        return <ReportStatusBadge status={study.dycovueReportStatus} isFlagged={isReadingQueueFlagged} size="small" />;
                                      })()}
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
                                        {study.claimedAt
                                          ? new Date(study.claimedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
                                          : study.assignedAt
                                            ? new Date(study.assignedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
                                            : '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontSize: '0.72rem' }}>{study.totalInstances ?? 0}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      {(() => {
                                        const isReadingQueueFlagged = study.facilityReviewStatus === 'flagged' || study.dycovueReport?.facilityReview?.status === 'flagged';
                                        return (
                                          <Tooltip title={isReadingQueueFlagged ? "Revise Flagged Report" : "View & Report"}>
                                            <IconButton
                                              size="small"
                                              color={isReadingQueueFlagged ? "error" : "primary"}
                                              onClick={(e) => { e.stopPropagation(); handleViewStudy(study); }}
                                              sx={{ width: 22, height: 22, p: 0, ...(isReadingQueueFlagged ? { bgcolor: 'rgba(239, 68, 68, 0.1)', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' } } : {}) }}
                                            >
                                              {isReadingQueueFlagged ? <Flag sx={{ fontSize: 13, color: '#EF4444' }} /> : <ViewReportIcon sx={{ fontSize: 13 }} />}
                                            </IconButton>
                                          </Tooltip>
                                        );
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <TablePagination
                            rowsPerPageOptions={[10, 20, 25, 50, 100]}
                            component="div"
                            count={filteredMyStudies.length}
                            rowsPerPage={rowsPerPageMyStudies}
                            page={pageMyStudies}
                            onPageChange={(e, newPage) => setPageMyStudies(newPage)}
                            onRowsPerPageChange={(e) => {
                              setRowsPerPageMyStudies(parseInt(e.target.value, 10));
                              setPageMyStudies(0);
                            }}
                            sx={{
                              borderTop: `1px solid ${themeObj.palette.divider}`,
                              minHeight: '28px',
                              '& .MuiTablePagination-toolbar': {
                                minHeight: '28px',
                                px: { xs: 1, sm: 1.5 },
                              },
                              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: '0.72rem',
                              },
                            }}
                          />
                        </>
                      )}
                      </TabPanel>

                      {/* Tab 1: Available Studies — direct routing feed from dedicated facilities */}
                      <TabPanel value={activeTab} index={1}>
                        {assignedHospitals.length === 0 ? (
                          <Box sx={{ p: 4, textAlign: 'center' }}>
                            <LocalHospital sx={{ fontSize: 56, color: 'text.secondary', mb: 2, opacity: 0.6 }} />
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                              No Dedicated Hospitals Assigned Yet
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 520, mx: 'auto', mb: 2.5 }}>
                              When an administrator assigns hospitals to you as their dedicated in-house radiologist, their incoming orders and DICOM scans will automatically stream directly here without requiring any manual requests.
                            </Typography>
                            <Button
                              variant="outlined"
                              startIcon={<Refresh />}
                              onClick={() => {
                                loadAssignedHospitals();
                                loadAssignedHospitalStudies();
                              }}
                              sx={{ borderRadius: '10px' }}
                            >
                              Check For Hospital Assignments
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ px: 1, pb: 0.5 }}>
                            {/* Top Controls Bar */}
                            <Box sx={{ p: '3px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                              <Box display="flex" alignItems="center" gap={0.75}>
                                <LocalHospital sx={{ color: 'primary.main', fontSize: 16 }} />
                                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
                                  Dedicated Hospital Worklist
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <Select
                                    value={assignedHospitalFilterStatus}
                                    onChange={(e) => {
                                      const newStatus = e.target.value;
                                      setAssignedHospitalFilterStatus(newStatus);
                                      loadAssignedHospitalStudies(selectedHospitalId, newStatus);
                                    }}
                                    sx={{ borderRadius: '4px', height: 24, fontSize: '0.72rem' }}
                                  >
                                    <MenuItem value="all" sx={{ fontSize: '0.72rem' }}>All Statuses</MenuItem>
                                    <MenuItem value="requested" sx={{ fontSize: '0.72rem' }}>Pending / Unclaimed</MenuItem>
                                    <MenuItem value="in_progress" sx={{ fontSize: '0.72rem' }}>In Progress</MenuItem>
                                    <MenuItem value="completed" sx={{ fontSize: '0.72rem' }}>Completed</MenuItem>
                                  </Select>
                                </FormControl>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Refresh sx={{ fontSize: 13 }} />}
                                  onClick={() => loadAssignedHospitalStudies(selectedHospitalId, assignedHospitalFilterStatus)}
                                  disabled={assignedHospitalStudiesLoading}
                                  sx={{ borderRadius: '4px', height: 24, fontSize: '0.72rem', py: 0, px: 1, textTransform: 'none' }}
                                >
                                  Refresh
                                </Button>
                              </Stack>
                            </Box>

                            {/* Hospital selector pills */}
                            <Box sx={{ px: '8px', pb: 0.5, display: 'flex', gap: 0.75, overflowX: 'auto' }}>
                              <Button
                                size="small"
                                variant={selectedHospitalId === 'all' ? 'contained' : 'outlined'}
                                onClick={() => {
                                  setSelectedHospitalId('all');
                                  loadAssignedHospitalStudies('all', assignedHospitalFilterStatus);
                                }}
                                sx={{
                                  borderRadius: '4px',
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  fontSize: '0.68rem',
                                  height: '22px',
                                  py: 0,
                                  px: 0.8,
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                              >
                                All Assigned ({assignedHospitals.reduce((acc, h) => acc + (h.totalStudies || 0), 0)})
                              </Button>

                              {assignedHospitals.map((hospital) => {
                                const isSelected = selectedHospitalId === hospital._id;
                                return (
                                  <Button
                                    key={hospital._id}
                                    size="small"
                                    variant={isSelected ? 'contained' : 'outlined'}
                                    onClick={() => {
                                      setSelectedHospitalId(hospital._id);
                                      loadAssignedHospitalStudies(hospital._id, assignedHospitalFilterStatus);
                                    }}
                                    sx={{
                                      borderRadius: '4px',
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      fontSize: '0.68rem',
                                      height: '22px',
                                      py: 0,
                                      px: 0.8,
                                      whiteSpace: 'nowrap',
                                      flexShrink: 0
                                    }}
                                  >
                                    {hospital.facilityName || hospital.name} ({hospital.totalStudies || 0})
                                  </Button>
                                );
                              })}
                            </Box>

                            {/* Studies Table */}
                            {assignedHospitalStudiesLoading ? (
                              <Box display="flex" justifyContent="center" py={6}>
                                <CircularProgress />
                              </Box>
                            ) : filteredAssignedStudies.length === 0 ? (
                              <Alert severity="info" sx={{ my: 2, borderRadius: '8px', py: 0.5, fontSize: '0.75rem' }}>
                                No studies found for this selection or status filter.
                              </Alert>
                            ) : (
                              <>
                                <TableContainer sx={{ px: 0.5 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ '& th': { py: '2px !important', px: '6px !important', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' } }}>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Priority</TableCell>
                                      <TableCell>Hospital</TableCell>
                                      <TableCell>Patient</TableCell>
                                      <TableCell>Modality</TableCell>
                                      <TableCell>Description</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Study Date</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Images</TableCell>
                                      <TableCell sx={{ textAlign: 'right' }}>Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {paginatedAssignedStudies.map((study) => {
                                      const isReported = study.dycovueReportStatus === 'completed';

                                      return (
                                        <TableRow
                                          key={study._id}
                                          hover
                                          sx={{ '& td': { py: '1.5px !important', px: '6px !important', fontSize: '0.72rem', height: '26px' }, cursor: 'pointer' }}
                                          onClick={() => handleClaimAndReportAssigned(study)}
                                        >
                                          <TableCell>
                                            {study.dycovuePriority === 'stat' ? (
                                              <Chip
                                                label="STAT"
                                                color="error"
                                                size="small"
                                                icon={<PriorityHigh sx={{ fontSize: 12 }} />}
                                                sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, px: '2px' }}
                                              />
                                            ) : (
                                              <Chip label="Routine" size="small" sx={{ height: 18, fontSize: '0.62rem', px: '2px' }} />
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                              <LocalHospital sx={{ fontSize: 12, color: 'primary.main' }} />
                                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 130, fontSize: '0.72rem' }}>
                                                 {study.facility?.facilityName || study.facility?.name || 'Assigned Hospital'}
                                               </Typography>
                                            </Box>
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {(() => {
                                              const currentUserId = user?._id || user?.id;
                                              const currentUserIdStr = currentUserId?.toString();
                                              const assignedId = (study.assignedRadiologist?._id || study.assignedRadiologist)?.toString();
                                              const isClaimedByMe = Boolean(assignedId && currentUserIdStr && assignedId === currentUserIdStr);
                                              const isClaimedByOther = Boolean(assignedId && currentUserIdStr && assignedId !== currentUserIdStr && ['claimed', 'in_progress'].includes(study.dycovueReportStatus));
                                              const assignedName = study.assignedRadiologist?.firstName
                                                ? `Dr. ${study.assignedRadiologist.firstName} ${study.assignedRadiologist.lastName || ''}`
                                                : (study.assignedRadiologistName ? `Dr. ${study.assignedRadiologistName}` : 'Another Radiologist');

                                              return (
                                                <Tooltip title={
                                                  isClaimedByOther
                                                    ? `Study claimed by ${assignedName} — Click to Review Lock`
                                                    : isClaimedByMe
                                                    ? 'Study claimed by you — Click to Write Report'
                                                    : 'Click to Claim Study for Reporting'
                                                }>
                                                  <Box
                                                    component="span"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (isClaimedByMe) {
                                                        handleClaimAndReportAssigned(study);
                                                      } else {
                                                        setClaimModalState({ open: true, study });
                                                      }
                                                    }}
                                                    sx={{
                                                      display: 'inline-flex',
                                                      alignItems: 'center',
                                                      gap: 0.5,
                                                      cursor: 'pointer',
                                                      '&:hover': {
                                                        '& .patient-name-text': {
                                                          color: '#3B82F6',
                                                          textDecoration: 'underline'
                                                        }
                                                      }
                                                    }}
                                                  >
                                                    <Box component="span" className="patient-name-text" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
                                                      {formatPatientName(study.patientName)}
                                                    </Box>
                                                    {study.patientId && (
                                                      <Box component="span" sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>
                                                        #{study.patientId}
                                                      </Box>
                                                    )}
                                                  </Box>
                                                </Tooltip>
                                              );
                                            })()}
                                          </TableCell>
                                           <TableCell>
                                             <Chip
                                               label={study.modality}
                                               size="small"
                                               color="primary"
                                               variant="outlined"
                                               sx={{ height: 18, fontSize: '0.62rem', px: '2px' }}
                                             />
                                           </TableCell>
                                           <TableCell>
                                             <Typography variant="body2" noWrap sx={{ maxWidth: 160, fontSize: '0.72rem' }}>
                                               {study.studyDescription || '—'}
                                             </Typography>
                                           </TableCell>
                                          <TableCell>
                                            {(() => {
                                              const currentUserId = user?._id || user?.id;
                                              const currentUserIdStr = currentUserId?.toString();
                                              const assignedId = (study.assignedRadiologist?._id || study.assignedRadiologist)?.toString();
                                              const isClaimedByMe = Boolean(assignedId && currentUserIdStr && assignedId === currentUserIdStr);
                                              const isClaimedByOther = Boolean(assignedId && currentUserIdStr && assignedId !== currentUserIdStr && ['claimed', 'in_progress'].includes(study.dycovueReportStatus));
                                              const isStudyFlagged = study.facilityReviewStatus === 'flagged' || study.dycovueReport?.facilityReview?.status === 'flagged';
                                              const isStudyAccepted = study.facilityReviewStatus === 'approved' || study.dycovueReport?.facilityReview?.status === 'approved';
                                              const flagConcerns = study.dycovueReport?.facilityReview?.concerns;
                                              const assignedName = study.assignedRadiologist?.firstName
                                                ? `Dr. ${study.assignedRadiologist.firstName} ${study.assignedRadiologist.lastName || ''}`
                                                : (study.assignedRadiologistName ? `Dr. ${study.assignedRadiologistName}` : 'Another Radiologist');

                                              return (
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                  {isStudyFlagged ? (
                                                    <Tooltip title={`Hospital Flagged: ${flagConcerns || 'Revision Requested'}`}>
                                                      <span>
                                                        <ReportStatusBadge status={study.dycovueReportStatus || 'requested'} isFlagged={true} size="small" />
                                                      </span>
                                                    </Tooltip>
                                                  ) : isClaimedByMe ? (
                                                    <Tooltip title="Claimed by you — Click to Review Lock">
                                                      <Chip
                                                        label="Claimed (You)"
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); setClaimModalState({ open: true, study }); }}
                                                        sx={{
                                                          height: 18,
                                                          fontSize: '0.62rem',
                                                          fontWeight: 700,
                                                          px: '3px',
                                                          bgcolor: 'rgba(16,185,129,0.15)',
                                                          color: '#10B981',
                                                          border: '1px solid rgba(16,185,129,0.3)',
                                                          cursor: 'pointer'
                                                        }}
                                                      />
                                                    </Tooltip>
                                                  ) : isClaimedByOther ? (
                                                    <Tooltip title={`Claimed by ${assignedName} — Click to Review Lock`}>
                                                      <Chip
                                                        label="Claimed"
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); setClaimModalState({ open: true, study }); }}
                                                        sx={{
                                                          height: 18,
                                                          fontSize: '0.62rem',
                                                          fontWeight: 700,
                                                          px: '3px',
                                                          bgcolor: 'rgba(239,68,68,0.15)',
                                                          color: '#EF4444',
                                                          border: '1px solid rgba(239,68,68,0.3)',
                                                          cursor: 'pointer'
                                                        }}
                                                      />
                                                    </Tooltip>
                                                  ) : (
                                                    <ReportStatusBadge status={study.dycovueReportStatus || 'requested'} isFlagged={false} size="small" />
                                                  )}
                                                  {isStudyAccepted && (
                                                    <Tooltip title="Hospital Accepted (All Clear)">
                                                      <Chip
                                                        label="Accepted"
                                                        color="success"
                                                        size="small"
                                                        icon={<CheckCircle sx={{ fontSize: 11 }} />}
                                                        sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600, px: '2px' }}
                                                      />
                                                    </Tooltip>
                                                  )}
                                                </Stack>
                                              );
                                            })()}
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
                                              {study.studyDate
                                                ? new Date(study.studyDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
                                                : '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" sx={{ fontSize: '0.72rem' }}>{study.totalInstances ?? 0}</Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                              {study.studyInstanceUID && (
                                                <Tooltip title="View Images in OHIF">
                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      window.open(generateOHIFUrl(study.studyInstanceUID), '_blank');
                                                    }}
                                                    sx={{ width: 22, height: 22, padding: '1px', borderRadius: '4px' }}
                                                  >
                                                    <Visibility sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Tooltip>
                                              )}
                                               {(() => {
                                                  const currentUserId = user?._id || user?.id;
                                                  const currentUserIdStr = currentUserId?.toString();
                                                  const assignedId = (study.assignedRadiologist?._id || study.assignedRadiologist)?.toString();
                                                  const isClaimedByMe = Boolean(assignedId && currentUserIdStr && assignedId === currentUserIdStr);
                                                  const isClaimedByOther = Boolean(assignedId && currentUserIdStr && assignedId !== currentUserIdStr && ['claimed', 'in_progress'].includes(study.dycovueReportStatus));
                                                  const isReported = study.dycovueReportStatus === 'completed';
                                                  const isStudyFlagged = study.facilityReviewStatus === 'flagged' || study.dycovueReport?.facilityReview?.status === 'flagged';
                                                  const assignedName = study.assignedRadiologist?.firstName
                                                    ? `Dr. ${study.assignedRadiologist.firstName} ${study.assignedRadiologist.lastName || ''}`
                                                    : (study.assignedRadiologistName ? `Dr. ${study.assignedRadiologistName}` : 'Another Radiologist');

                                                  if (isStudyFlagged) {
                                                    return (
                                                      <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="error"
                                                        startIcon={<Flag sx={{ fontSize: 12 }} />}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleOpenReportViewer(study);
                                                        }}
                                                        sx={{
                                                          borderRadius: '4px',
                                                          fontSize: '0.68rem',
                                                          height: '22px',
                                                          py: 0,
                                                          px: 0.8,
                                                          textTransform: 'none',
                                                          fontWeight: 700,
                                                          whiteSpace: 'nowrap',
                                                          bgcolor: '#EF4444',
                                                          color: '#FFFFFF',
                                                          '&:hover': { bgcolor: '#DC2626' }
                                                        }}
                                                      >
                                                        Revise Report
                                                      </Button>
                                                    );
                                                  }

                                                  if (isReported) {
                                                    return (
                                                      <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        startIcon={<Visibility sx={{ fontSize: 12 }} />}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleOpenReportViewer(study);
                                                        }}
                                                        sx={{
                                                          borderRadius: '4px',
                                                          fontSize: '0.68rem',
                                                          height: '22px',
                                                          py: 0,
                                                          px: 0.8,
                                                          textTransform: 'none',
                                                          fontWeight: 700,
                                                          whiteSpace: 'nowrap',
                                                        }}
                                                      >
                                                        View Report
                                                      </Button>
                                                    );
                                                  }

                                                  if (isClaimedByOther) {
                                                    return (
                                                      <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={<Lock sx={{ fontSize: 12 }} />}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setClaimModalState({ open: true, study });
                                                        }}
                                                        sx={{
                                                          borderRadius: '4px',
                                                          fontSize: '0.66rem',
                                                          height: '22px',
                                                          py: 0,
                                                          px: 0.8,
                                                          textTransform: 'none',
                                                          fontWeight: 600,
                                                          whiteSpace: 'nowrap',
                                                        }}
                                                      >
                                                        Locked ({assignedName})
                                                      </Button>
                                                    );
                                                  }

                                                  if (isClaimedByMe) {
                                                    return (
                                                      <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Tooltip title="View Claim / Release Lock">
                                                          <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              setClaimModalState({ open: true, study });
                                                            }}
                                                            sx={{
                                                              width: 22,
                                                              height: 22,
                                                              padding: '1px',
                                                              borderRadius: '4px',
                                                              bgcolor: 'rgba(16,185,129,0.1)',
                                                              color: '#10B981',
                                                              '&:hover': { bgcolor: 'rgba(16,185,129,0.2)' }
                                                            }}
                                                          >
                                                            <LockOpen sx={{ fontSize: 14 }} />
                                                          </IconButton>
                                                        </Tooltip>
                                                        <Button
                                                          size="small"
                                                          variant="contained"
                                                          color="primary"
                                                          startIcon={<Assignment sx={{ fontSize: 12 }} />}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleClaimAndReportAssigned(study);
                                                          }}
                                                          sx={{
                                                            borderRadius: '4px',
                                                            fontSize: '0.68rem',
                                                            height: '22px',
                                                            py: 0,
                                                            px: 0.8,
                                                            textTransform: 'none',
                                                            fontWeight: 700,
                                                            whiteSpace: 'nowrap',
                                                          }}
                                                        >
                                                          {study.dycovueReportStatus === 'in_progress' ? 'Continue' : 'Start Report'}
                                                        </Button>
                                                      </Stack>
                                                    );
                                                  }

                                                  // Unclaimed / Available: Show prominent Claim Study button
                                                  return (
                                                    <Button
                                                      size="small"
                                                      variant="contained"
                                                      color="primary"
                                                      startIcon={<Assignment sx={{ fontSize: 12 }} />}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setClaimModalState({ open: true, study });
                                                      }}
                                                      sx={{
                                                        borderRadius: '4px',
                                                        fontSize: '0.68rem',
                                                        height: '22px',
                                                        py: 0,
                                                        px: 0.8,
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        whiteSpace: 'nowrap',
                                                      }}
                                                    >
                                                      Claim Study
                                                    </Button>
                                                  );
                                                })()}
                                            </Stack>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              <TablePagination
                                rowsPerPageOptions={[10, 20, 25, 50, 100]}
                                component="div"
                                count={filteredAssignedStudies.length}
                                rowsPerPage={rowsPerPageAssigned}
                                page={pageAssigned}
                                onPageChange={(e, newPage) => setPageAssigned(newPage)}
                                onRowsPerPageChange={(e) => {
                                  setRowsPerPageAssigned(parseInt(e.target.value, 10));
                                  setPageAssigned(0);
                                }}
                                sx={{
                                  borderTop: `1px solid ${themeObj.palette.divider}`,
                                  minHeight: '28px',
                                  '& .MuiTablePagination-toolbar': {
                                    minHeight: '28px',
                                    px: { xs: 1, sm: 2 },
                                  },
                                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                    fontSize: '0.7rem',
                                  },
                                }}
                              />
                            </>
                            )}
                          </Box>
                        )}
                      </TabPanel>

                      {/* Tab 2: Scan Board — facility-wide PACS studies (permission-gated) */}
                      {canViewAllStudies && (
                        <TabPanel value={activeTab} index={2}>
                          {allStudiesLoading ? (
                            <Box display="flex" justifyContent="center" py={6}>
                              <CircularProgress />
                            </Box>
                          ) : allStudies.length === 0 ? (
                            <Alert severity="info" sx={{ m: 3, borderRadius: '12px' }}>
                              No studies found in your facility.
                            </Alert>
                          ) : (
                            <>
                              <Box sx={{ p: '3px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, fontSize: '0.68rem' }}>
                                  {allStudies.length} studies in your facility
                                </Typography>
                                <Button size="small" startIcon={<Refresh sx={{ fontSize: 13 }} />} onClick={loadAllStudies} disabled={allStudiesLoading} sx={{ height: 24, fontSize: '0.68rem', py: 0, px: 1, borderRadius: '4px', textTransform: 'none' }}>
                                  Refresh
                                </Button>
                              </Box>
                              <TableContainer sx={{ px: 0.5 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ '& th': { py: '2px !important', px: '6px !important', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' } }}>
                                      <TableCell>Patient</TableCell>
                                      <TableCell>Modality</TableCell>
                                      <TableCell>Description</TableCell>
                                      <TableCell>Report Status</TableCell>
                                      <TableCell>Assigned To</TableCell>
                                      <TableCell>Study Date</TableCell>
                                      <TableCell>Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {paginatedAllStudies.map((study) => (
                                      <TableRow key={study._id} hover sx={{ '& td': { py: '1.5px !important', px: '6px !important', fontSize: '0.72rem', height: '26px' } }}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                             <Box component="span" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
                                               {formatPatientName(study.patientName)}
                                             </Box>
                                             {study.patientId && (
                                               <Box component="span" sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>
                                                 #{study.patientId}
                                               </Box>
                                             )}
                                           </Box>
                                         </TableCell>
                                         <TableCell>
                                           <Chip label={study.modality} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.62rem', px: '2px' }} />
                                         </TableCell>
                                         <TableCell>
                                           <Typography variant="body2" noWrap sx={{ maxWidth: 180, fontSize: '0.72rem' }}>
                                             {study.studyDescription || '—'}
                                           </Typography>
                                         </TableCell>
                                        <TableCell>
                                          {(() => {
                                            const isAllStudyFlagged = study.facilityReviewStatus === 'flagged' || study.dycovueReport?.facilityReview?.status === 'flagged';
                                            return <ReportStatusBadge status={study.dycovueReportStatus} isFlagged={isAllStudyFlagged} size="small" />;
                                          })()}
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.68rem' }}>
                                            {study.assignedRadiologist
                                              ? `${study.assignedRadiologist.firstName || ''} ${study.assignedRadiologist.lastName || ''}`.trim() || 'Assigned'
                                              : study.dycovueReportStatus === 'requested' ? 'Unassigned' : '—'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
                                            {study.studyDate
                                              ? new Date(study.studyDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
                                              : '—'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Tooltip title="View & Report">
                                            <IconButton size="small" color="primary" onClick={() => handleViewStudy(study)} sx={{ width: 22, height: 22, padding: '1px', borderRadius: '4px' }}>
                                              <ViewReportIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              <TablePagination
                                rowsPerPageOptions={[10, 20, 25, 50, 100]}
                                component="div"
                                count={filteredAllStudies.length}
                                rowsPerPage={rowsPerPageAllStudies}
                                page={pageAllStudies}
                                onPageChange={(e, newPage) => setPageAllStudies(newPage)}
                                onRowsPerPageChange={(e) => {
                                  setRowsPerPageAllStudies(parseInt(e.target.value, 10));
                                  setPageAllStudies(0);
                                }}
                                sx={{
                                  borderTop: `1px solid ${themeObj.palette.divider}`,
                                  minHeight: '28px',
                                  '& .MuiTablePagination-toolbar': {
                                    minHeight: '28px',
                                    px: { xs: 1, sm: 2 },
                                  },
                                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                    fontSize: '0.7rem',
                                  },
                                }}
                              />
                            </>
                          )}
                        </TabPanel>
                      )}

                      {/* QA Review Tab Panel — index 3 (with scanBoard) or 2 (without scanBoard) */}
                      <TabPanel value={activeTab} index={canViewAllStudies ? 3 : 2}>
                        <QAQueue
                          onStudyClick={handleViewStudy}
                          onEditReport={(study) => {
                            setSelectedStudy(study);
                            setReportFormOpen(true);
                          }}
                        />
                      </TabPanel>
                    </>
                  )}
                </GlassPaper>

                {/* Report Form Dialog */}
                {selectedStudy && (
                  <DycovueReportForm
                    open={reportFormOpen}
                    onClose={() => {
                      setReportFormOpen(false);
                      setSelectedStudy(null);
                    }}
                    study={selectedStudy}
                    onSuccess={handleReportSuccess}
                  />
                )}
              </Container>
            )}
          </Box>
        </Box>
        <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
        <LabReportModal
          open={labReportModalOpen}
          onClose={() => setLabReportModalOpen(false)}
          resultId={selectedLabReportId}
          patientName={selectedLabPatientName}
          isDark={mode === 'dark'}
        />
        <ClaimStudyModal
          open={claimModalState.open}
          onClose={() => setClaimModalState({ open: false, study: null })}
          study={claimModalState.study}
          currentUserId={user?._id || user?.id}
          onConfirmClaim={handleConfirmClaimFromModal}
          onUnclaim={handleConfirmUnclaimFromModal}
          onViewOnly={handleViewOnlyFromModal}
          isDark={mode === 'dark'}
        />

        {/* Report Viewer Dialog */}
        <Dialog
          open={reportViewerOpen}
          onClose={() => {
            setReportViewerOpen(false);
            setReportViewerStudy(null);
          }}
          maxWidth={false}
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
              width: { xs: '95vw', sm: '92vw', md: '860px', lg: '920px' },
              maxWidth: '940px !important',
              maxHeight: '88vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              bgcolor: mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
              backgroundImage: 'none'
            }
          }}
        >
          <DialogTitle sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}` }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={1.25}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.98rem' }}>
                  DycoVue In-House Report Viewer
                </Typography>
                {reportViewerStudy?.patientName && (
                  <Chip
                    label={formatPatientName(reportViewerStudy.patientName)}
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.72rem', height: 20 }}
                  />
                )}
                {reportViewerStudy?.modality && (
                  <Chip
                    label={reportViewerStudy.modality}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 700, fontSize: '0.72rem', height: 20 }}
                  />
                )}
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setReportViewerOpen(false);
                  setReportViewerStudy(null);
                }}
              >
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 1.5, sm: 2 }, overflowY: 'auto', flexGrow: 1 }}>
            {reportViewerStudy && (
              <ReportViewer
                studyId={reportViewerStudy._id || reportViewerStudy.pacsStudyId || reportViewerStudy.studyInstanceUID}
                study={reportViewerStudy}
                reportType="dycovue-report"
                onEditDycovueReport={(s) => {
                  setReportViewerOpen(false);
                  setSelectedStudy(s || reportViewerStudy);
                  setReportFormOpen(true);
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default ReaderPortal;
